import type { QueryClient } from '@tanstack/react-query';

export async function refreshRepositoryLifecycle(client: QueryClient): Promise<void> {
  await Promise.all(
    [
      'projects',
      'repository-lineage',
      'repository-lifecycle-runs',
      'projects-health',
      'codebases',
      'projectSources',
    ].map(key => client.invalidateQueries({ queryKey: [key] }))
  );
}

/** A synchronous latch closes the gap before React renders pending state. */
export function singleFlight(): (action: () => Promise<void>) => Promise<void> {
  let pending = false;
  return async action => {
    if (pending) return;
    pending = true;
    try {
      await action();
    } finally {
      pending = false;
    }
  };
}

export function lifecycleError(error: unknown): string {
  const status =
    typeof error === 'object' && error !== null && 'status' in error ? error.status : null;
  if (status === 401 || status === 403)
    return 'An authenticated administrator in this organization must approve remote changes.';
  if (status === 409)
    return 'Conflict: the request or repository has changed. Refresh inventory and review an exact new request.';
  if (status === 503)
    return 'Repository provider is not configured. An operator must configure the trusted lifecycle adapters.';
  if (status === 400 || status === 422)
    return 'Request rejected. Check the approved source, exact release, names, and paths.';
  if (status === 404) return 'Project unavailable or no longer accessible. Refresh projects.';
  return 'Provider operation failed or reconciliation is required. Reconcile the recorded run, then retry this unchanged request to recover its receipt.';
}

export const exactSha = /^(?:[a-f0-9]{40}|[a-f0-9]{64})$/;
export function validRelease(ref: string, sha: string): boolean {
  return (
    /^refs\/tags\/[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(ref) &&
    ref.length <= 200 &&
    !ref.includes('..') &&
    !ref.includes('//') &&
    !ref.endsWith('/') &&
    !ref.endsWith('.lock') &&
    exactSha.test(sha)
  );
}

/** Never render raw provider JSON/errors or credential-bearing links. */
export function repositoryLink(value: string): string | null {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash
      ? value
      : null;
  } catch {
    return null;
  }
}
